import type { IVerbaScholaLessonGeneratedMaterial, IVerbaScholaLessonPlanSection } from '../api/VerbaScholaApi';

/** Post-class notes: never required before exercises and never bulk-marked. */
export const TEACHER_REFLECTION_TITLE = 'Teacher reflection';

export interface PlanReadiness {
    /** Saved plan sections (except Teacher reflection) and materials. */
    required: number;
    /** Required plan sections, without materials; an approved plan needs one. */
    sections: number;
    /** Required items marked Ready that still have text. */
    ready: number;
    /** Unready items with text, which Mark all ready will mark. */
    markable: number;
    /** Items without text, even if marked Ready; the teacher must write or delete them. */
    needsContent: string[];
    /** True only when at least one plan section exists and every required item is Ready with text. */
    allReady: boolean;
}

/** Mirrors the server's mark_plan_ready rules so the gate matches the bulk action. */
export function planReadiness(sections: IVerbaScholaLessonPlanSection[], materials: IVerbaScholaLessonGeneratedMaterial[]): PlanReadiness {
    const planSections = sections.filter(section => section.title !== TEACHER_REFLECTION_TITLE);
    const items = [
        ...planSections.map(section => ({ title: section.title, marked: section.readiness_status === 'ready', written: Boolean(section.body?.trim()) })),
        ...materials.map(material => ({ title: material.title, marked: material.status === 'ready', written: Boolean(material.body_text?.trim()) })),
    ];
    // A Ready flag on a card whose text was later cleared does not approve it.
    const ready = items.filter(item => item.marked && item.written).length;
    return {
        required: items.length,
        sections: planSections.length,
        ready,
        markable: items.filter(item => !item.marked && item.written).length,
        needsContent: items.filter(item => !item.written).map(item => item.title),
        allReady: planSections.length > 0 && ready === items.length,
    };
}

/**
 * A new draft needs saved plan content before exercise authoring. Its cards can
 * remain Draft while exercises are generated and reviewed. Existing exercises
 * (including older imports) and published lessons stay editable.
 */
export function exercisesLockReason(readiness: PlanReadiness, exerciseCount: number, lessonStatus: string): string | null {
    if (lessonStatus !== 'draft' || readiness.sections > 0 || exerciseCount > 0) return null;
    return 'Add at least one lesson plan section before exercises.';
}
